import React, { useCallback, useEffect, useState } from 'react'
import { useSelector, useStore } from 'react-redux'
import { ApolloProvider } from '@apollo/client'
import { get, isNil } from 'lodash'
import PropTypes from 'prop-types'

import {
  getEnvironmentVariable,
  showErrorNotification,
} from '../../core/helpers'
import { firebase } from '../../core/services'

import {
  aiHttpService,
  apolloService,
  httpService,
} from '../../common/services'

import { useRefreshTokenMutation } from '../../account/hooks'

import MetadataProvider from './metadata-provider'

const Bootstrap = ({ children }) => {
  const [possibleTypesError, setPossibleTypesError] = useState()
  const [isTokenSet, setIsTokenSet] = useState(false)
  const [isMounting, setIsMounting] = useState(true)
  const store = useStore()

  const { accessToken, refreshToken, data } = useSelector(
    store => store?.account?.user,
  )

  const [refreshAccessToken, { error }] = useRefreshTokenMutation()

  const getAccessToken = useCallback(
    () => get(store.getState(), 'account.user.accessToken'),
    [store],
  )

  const getNewAccessToken = useCallback(
    () => refreshAccessToken(refreshToken),
    [refreshAccessToken, refreshToken],
  )

  const getAiAccessToken = useCallback(
    () =>
      `Bearer ${getEnvironmentVariable(
        'REACT_APP_AI_SERVICE_AUTHORIZATION_TOKEN',
      )}`,
    [],
  )

  useEffect(() => {
    const initialize = async () => {
      try {
        await apolloService.client.addPossibleTypesToCache()
        setIsMounting(false)
      } catch (error) {
        setPossibleTypesError(error)
      }
    }

    if (isMounting && isNil(possibleTypesError)) {
      initialize()
    }
  }, [isMounting, possibleTypesError])

  useEffect(() => {
    if (!isNil(accessToken)) {
      apolloService.client.addAccessTokenToHeaders(
        getAccessToken,
        getNewAccessToken,
      )

      aiHttpService.client.addAccessTokenToHeaders(
        getAiAccessToken,
        getAiAccessToken,
      )

      httpService.addAccessTokenToHeaders(getAccessToken, getNewAccessToken)

      if (!isNil(data?.id)) {
        firebase.setUserId(data.id)
        firebase.setUserProperty(
          'screenSize',
          `${window.screen?.width}x${window.screen?.height}`,
        )
      }

      setIsTokenSet(true)
    } else {
      setIsTokenSet(false)
      apolloService.client.removeAccessTokenFromHeaders()
    }
  }, [
    accessToken,
    data,
    getAccessToken,
    getAiAccessToken,
    getNewAccessToken,
    refreshAccessToken,
    refreshToken,
  ])

  useEffect(() => {
    if (error) {
      showErrorNotification({
        title: 'User error',
        description: error,
      })
    }
  }, [error])

  if (!isNil(possibleTypesError)) {
    throw possibleTypesError
  }

  if (isMounting || (!isTokenSet && !isNil(accessToken))) {
    return null
  }

  return (
    <ApolloProvider client={apolloService.client.client}>
      <MetadataProvider>{children}</MetadataProvider>
    </ApolloProvider>
  )
}

Bootstrap.propTypes = {
  children: PropTypes.node.isRequired,
}

export default Bootstrap
